/**
 * EnquiriesPage Component
 * Main page for managing enquiries in collins-mobile
 * Replicates functionality from collins-admin with collins-mobile styling patterns
 */

import React, { useCallback, useMemo } from "react";

import { PageContainer } from "../../components/Layout/PageContainer/PageContainer";
import { useAppContext } from "../../hooks/useAppContext";
import { usePermissions } from "../../hooks/usePermissions";
import { useUserContext } from "../../hooks/useUserContext";
import { useEnquiriesState } from "./useEnquiriesState";
import { EnquiriesHeader } from "./Header";
import { EnquiriesSidebar, EnquiriesMain } from "./Content";
import { EnquiryStatus, EnquiriesSortOption } from "./types";

export function EnquiriesPage() {
  const {
    enquiries,
    loading,
    error,
    filters,
    sortOption,
    facetCounts,
    filteredTotalHeads,
    total,
    offset,
    limit,
    recentlyUpdatedIds,
    updateFilter,
    batchUpdateFilters,
    clearFilters,
    updateSort,
    onPaginate,
    takeEnquiry,
    updateStatus,
    assignEnquiry,
    clearNotifications,
    bulkRejectEnquiries,
    bulkDeleteEnquiries,
    bulkAssignEnquiries,
    bulkApplyUpdates,
    bulkActionError,
    clearBulkActionError,
    isBulkApplying,
    refetch
  } = useEnquiriesState();

  const { venueGroupConfig } = useAppContext();
  const { user: currentUser } = useUserContext();
  const { roles, capabilities } = usePermissions();
  const isInternal = roles.derived.userIsInternal;
  const userCanReject =
    isInternal ||
    roles.organisation.update ||
    capabilities.manageVenueGroupPermissions.some;
  const userCanDelete = isInternal || roles.booking.delete;
  const users = useMemo(
    () => venueGroupConfig?.users ?? [],
    [venueGroupConfig?.users]
  );
  const hasBookingCustomFieldLabel = Boolean(
    venueGroupConfig?.booking_custom_field_label
  );

  const handleSortChange = useCallback(
    (value: EnquiriesSortOption) => {
      updateSort(value);
    },
    [updateSort]
  );

  const handleStatusFilterClick = useCallback(
    (status: EnquiryStatus) => {
      const currentStatuses = filters.status ?? [];
      const newStatuses = currentStatuses.includes(status)
        ? currentStatuses.filter((s) => s !== status)
        : [...currentStatuses, status];
      updateFilter("status", newStatuses.length > 0 ? newStatuses : undefined);
    },
    [filters.status, updateFilter]
  );

  return (
    <PageContainer
      header={{
        content: (
          <EnquiriesHeader
            sortOption={sortOption}
            facetCounts={facetCounts}
            onSortChange={handleSortChange}
            onRefresh={refetch}
          />
        )
      }}
      sidebar={{
        content: (
          <EnquiriesSidebar
            filters={filters}
            facetCounts={facetCounts}
            users={users}
            currentUserId={currentUser?._id}
            isInternal={isInternal}
            hasBookingCustomFieldLabel={hasBookingCustomFieldLabel}
            updateFilter={updateFilter}
            batchUpdateFilters={batchUpdateFilters}
          />
        )
      }}
      main={{
        content: (
          <EnquiriesMain
            enquiries={enquiries}
            users={users}
            loading={loading}
            error={error}
            filters={filters}
            total={total}
            offset={offset}
            limit={limit}
            recentlyUpdatedIds={recentlyUpdatedIds}
            filteredTotalHeads={filteredTotalHeads}
            currentUserId={currentUser?._id}
            isInternal={isInternal}
            userCanReject={userCanReject}
            userCanDelete={userCanDelete}
            filterDrawerContent={
              <EnquiriesSidebar
                filters={filters}
                facetCounts={facetCounts}
                users={users}
                currentUserId={currentUser?._id}
                isInternal={isInternal}
                hasBookingCustomFieldLabel={hasBookingCustomFieldLabel}
                updateFilter={updateFilter}
                batchUpdateFilters={batchUpdateFilters}
              />
            }
            takeEnquiry={takeEnquiry}
            updateStatus={updateStatus}
            assignEnquiry={assignEnquiry}
            clearNotifications={clearNotifications}
            bulkRejectEnquiries={bulkRejectEnquiries}
            bulkDeleteEnquiries={bulkDeleteEnquiries}
            bulkAssignEnquiries={bulkAssignEnquiries}
            bulkApplyUpdates={bulkApplyUpdates}
            bulkActionError={bulkActionError}
            clearBulkActionError={clearBulkActionError}
            isBulkApplying={isBulkApplying}
            bookingLabels={venueGroupConfig?.booking_labels ?? []}
            refetch={refetch}
            onPaginate={onPaginate}
            onStatusFilterClick={handleStatusFilterClick}
            onClearFilters={clearFilters}
          />
        )
      }}
    />
  );
}
